import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { motion } from 'framer-motion';
import { useAppDisplayModeContext, useStyles } from '@archipro-design/aria';
import * as S from './PaintPalette.style';
import {
    PaintColour,
    PaintColourPicker,
} from '@modules/paint-picker-new/component/paint-colours/PaintColour';
import {
    HINT_ANIMATION_DURATION,
    PALETTE_COLOUR_LIMIT,
} from '@modules/paint-picker-new/config';
import { usePaintPickerContext } from '@modules/paint-picker-new/context/PaintPickerContext';
import { PaintColourDesktopControls } from '@modules/paint-picker-new/component/paint-colours/PaintColourDesktopControls';
import { PaintColourMobileControls } from '@modules/paint-picker-new/component/paint-colours/PaintColourMobileControls';
import { useInViewport } from 'ahooks';

interface PaintPaletteProps {
    onOpen: () => void;
    paintsContainerRef: React.RefObject<HTMLUListElement>;
    isAnimating: boolean;
}

export const PaintPalette = ({
    onOpen,
    paintsContainerRef,
    isAnimating,
}: PaintPaletteProps) => {
    const { css } = useStyles();
    const { desktop } = useAppDisplayModeContext();

    const {
        state: {
            selectedColorIndex,
            usageHintEnabled,
            isPicking,
            isAddingToCart,
        },
        paintSelection,
        setSelectedColour,
        setUsageHint,
    } = usePaintPickerContext();

    const [clickedPaintTop, setClickedElementTop] = useState(0);
    const [isPaintsContainerInViewport] = useInViewport(paintsContainerRef, {
        threshold: 0.25,
    });

    const isAddingPaintAllowed = paintSelection.length < PALETTE_COLOUR_LIMIT;
    const paints = desktop ? paintSelection : [...paintSelection].reverse();
    const mousePositionRef = useRef({ x: 0, y: 0 });

    useEffect(() => {
        const updateMousePosition = (e: MouseEvent) => {
            mousePositionRef.current = { x: e.clientX, y: e.clientY };
        };

        window.addEventListener('mousemove', updateMousePosition);

        return () =>
            window.removeEventListener('mousemove', updateMousePosition);
    }, []);

    // hiding hint after some time
    const hintTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useLayoutEffect(() => {
        if (
            !hintTimeoutRef.current &&
            usageHintEnabled &&
            isPaintsContainerInViewport
        ) {
            hintTimeoutRef.current = setTimeout(
                () => setUsageHint(false),
                HINT_ANIMATION_DURATION * 1000,
            );
        }
    }, [usageHintEnabled, setUsageHint, isPaintsContainerInViewport]);

    const paintSelectionRef = useRef<typeof paintSelection>();

    // scroll paints container to the bottom when adding paints
    useEffect(() => {
        if (desktop) {
            const prevLength = paintSelectionRef.current?.length ?? 0;
            const currentLength = paintSelection.length;

            if (currentLength > prevLength && paintsContainerRef.current) {
                paintsContainerRef.current.scrollTop =
                    paintsContainerRef.current.scrollHeight;
            }

            paintSelectionRef.current = paintSelection;
        }
    }, [desktop, paintSelection, paintsContainerRef]);

    /**
     * Handles paint color selection on desktop.
     * Otherwise, calculates the position for paint controls and sets the selected color index.
     */
    const handlePaintHoverDesktop = useCallback(
        (e: React.MouseEvent<HTMLDivElement>, index: number) => {
            if (!isAnimating && !isPicking && paintsContainerRef.current) {
                const element = (e.currentTarget as HTMLElement).parentElement;

                if (element) {
                    const nameElement =
                        element.querySelector('.paint-colour-name');
                    const nameHeight = nameElement
                        ? nameElement.clientHeight
                        : 0;

                    // position & show paint controls
                    setClickedElementTop(
                        element.offsetTop -
                            paintsContainerRef.current.scrollTop +
                            nameHeight,
                    );
                    setSelectedColour(index);
                }
            }
        },
        [isAnimating, isPicking, paintsContainerRef, setSelectedColour],
    );

    const handlePaintDeselect = useCallback(
        (e: React.MouseEvent<HTMLElement>) => {
            if (!isPicking && !isAddingToCart) {
                const t = e.relatedTarget as HTMLElement;

                if (
                    t &&
                    !t.classList?.contains('paint-controls') &&
                    !t.closest('.paint-colour') &&
                    !t.closest('.paint-controls')
                ) {
                    // deselect the paint
                    setSelectedColour(null);
                }
            }
        },
        [isAddingToCart, isPicking, setSelectedColour],
    );

    const createManualMouseEvent = (): React.MouseEvent<HTMLElement> => {
        const { x, y } = mousePositionRef.current;
        return {
            clientX: x,
            clientY: y,
            relatedTarget: document.elementFromPoint(x, y) as HTMLElement,
        } as unknown as React.MouseEvent<HTMLElement>;
    };

    useEffect(() => {
        if (!isAddingToCart) {
            const manualEvent = createManualMouseEvent();
            handlePaintDeselect(manualEvent);
        }
    }, [isAddingToCart, handlePaintDeselect]);

    return (
        <div className={css(S.Container)}>
            <motion.ul
                ref={paintsContainerRef}
                className={css(S.ColoursList)}
                variants={desktop ? S.colourListAnimation : undefined}
                onScroll={() =>
                    selectedColorIndex !== null && setSelectedColour(null)
                }
                onMouseEnter={onOpen}
            >
                {!desktop && isAddingPaintAllowed ? (
                    <PaintColourPicker />
                ) : null}

                {paints?.map((paint, index) => {
                    return (
                        <li
                            key={'colour' + paint.id}
                            className={`${css(S.ColoursListElement)} paint-colour`}
                            onMouseLeave={handlePaintDeselect}
                        >
                            <PaintColour
                                {...paint}
                                onMouseEnter={(e) =>
                                    handlePaintHoverDesktop(e, index)
                                }
                            />
                            {!desktop && (
                                <PaintColourMobileControls paint={paint} />
                            )}
                        </li>
                    );
                })}

                {desktop && isAddingPaintAllowed && (
                    <li className={css(S.ColoursListElement)}>
                        <PaintColourPicker />
                    </li>
                )}
            </motion.ul>

            {desktop && (
                <PaintColourDesktopControls
                    top={clickedPaintTop}
                    onMouseLeave={handlePaintDeselect}
                />
            )}

            {desktop && (
                <motion.div
                    className={css(S.UsageHint)}
                    initial={{ opacity: 0 }}
                    animate={
                        usageHintEnabled ? ['hintOn', 'animate'] : 'hintOff'
                    }
                    variants={S.hintAnimation}
                >
                    Click to use Colour Selector
                </motion.div>
            )}
        </div>
    );
};
